import { FormEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Form, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../Hooks/axiosApi";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import {
  LoanProduct,
  LoanProductForm,
  Lender,
  buildLoanDetailsJsonFromSelection,
  buildLoanProductPayload,
  createDefaultPropertyDetails,
  createDefaultAssetSecuredDetails,
  createDefaultAssetUnsecuredDetails,
  loanDocumentationRows,
  normalizeLender,
  normalizeLoanProduct,
  parseLoanDetailsJsonToSelection,
  parsePropertyDetailsFromLoanDetailsJson,
  parseAssetSecuredDetailsFromLoanDetailsJson,
  parseAssetUnsecuredDetailsFromLoanDetailsJson,
  productTypeOptions,
  PropertyDetails,
  AssetSecuredDetails,
  AssetUnsecuredDetails,
  validateLoanProductForm,
  LoanDocumentationSelection,
} from "./loanProductUtils";
import PropertyProductTypeFields from "./PropertyProductTypeFields";
import AssetSecuredProductTypeFields from "./AssetSecuredProductTypeFields";
import AssetUnsecuredProductTypeFields from "./AssetUnsecuredProductTypeFields";

const EditCRMLoanProduct = () => {
  const navigate = useSafeNavigate();
  const { loanProductID } = useParams<{ loanProductID: string }>();
  const loanProductIdAsNumber = useMemo(() => Number(loanProductID), [loanProductID]);

  const [loanProduct, setLoanProduct] = useState<LoanProduct | null>(null);
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [documentationSelection, setDocumentationSelection] = useState<LoanDocumentationSelection>(
    parseLoanDetailsJsonToSelection("")
  );
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>(createDefaultPropertyDetails());
  const [assetSecuredDetails, setAssetSecuredDetails] = useState<AssetSecuredDetails>(createDefaultAssetSecuredDetails());
  const [assetUnsecuredDetails, setAssetUnsecuredDetails] = useState<AssetUnsecuredDetails>(
    createDefaultAssetUnsecuredDetails()
  );
  const [form, setForm] = useState<LoanProductForm>({ lenderId: "", productName: "", productType: "", loanDetailsJson: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof LoanProductForm, string>>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>("");



  useEffect(() => {
    const fetchData = async () => {
      if (!loanProductID || Number.isNaN(loanProductIdAsNumber) || loanProductIdAsNumber <= 0) {
        setApiError("Invalid loan product id.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setApiError("");

      try {
        const [productResponse, lendersResponse] = await Promise.all([
          api.get(`/api/Lenders/Getloan-product/${loanProductIdAsNumber}`),
          api.get("/api/Lenders/Getlenders"),
        ]);

        const lendersData = Array.isArray(lendersResponse?.data) ? lendersResponse.data : [];
        const productPayload = productResponse?.data;

        const found = Array.isArray(productPayload)
          ? productPayload.length > 0
            ? normalizeLoanProduct(productPayload[0])
            : null
          : productPayload
            ? normalizeLoanProduct(productPayload)
            : null;

        if (!found) {
          setApiError("Loan product not found.");
          setLoading(false);
          return;
        }

        const parsedSelection = parseLoanDetailsJsonToSelection(found.loanDetailsJson || "");
        const parsedPropertyDetails = parsePropertyDetailsFromLoanDetailsJson(found.loanDetailsJson || "");
        const parsedAssetSecured = parseAssetSecuredDetailsFromLoanDetailsJson(found.loanDetailsJson || "");
        const parsedAssetUnsecured = parseAssetUnsecuredDetailsFromLoanDetailsJson(found.loanDetailsJson || "");

        setLoanProduct(found);
        setDocumentationSelection(parsedSelection);
        setPropertyDetails(parsedPropertyDetails);
        setAssetSecuredDetails(parsedAssetSecured);
        setAssetUnsecuredDetails(parsedAssetUnsecured);
        const normalized = lendersData.map(normalizeLender).filter((item: Lender) => item.lenderId > 0);
        normalized.sort((a: Lender, b: Lender) => (a.lenderName || "").localeCompare(b.lenderName || ""));
        setLenders(normalized);
        setForm({
          lenderId: String(found.lenderId || ""),
          productName: found.productName || "",
          productType: found.productType || "",
          loanDetailsJson: buildLoanDetailsJsonFromSelection(
            parsedSelection,
            found.productType || "",
            parsedPropertyDetails,
            parsedAssetSecured,
            parsedAssetUnsecured
          ),
        });
      } catch (error: any) {
        setApiError(error?.response?.data?.message || "Failed to load loan product details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loanProductID, loanProductIdAsNumber]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      loanDetailsJson: buildLoanDetailsJsonFromSelection(
        documentationSelection,
        prev.productType,
        propertyDetails,
        assetSecuredDetails,
        assetUnsecuredDetails
      ),
    }));
  }, [documentationSelection, propertyDetails, assetSecuredDetails, assetUnsecuredDetails]);

  const handleChange = (name: keyof LoanProductForm, value: string) => {
    let nextValue = value;

    if (name === "productName") {
      nextValue = value.slice(0, 100);
    }

    setForm((prev) => {
      const nextForm = { ...prev, [name]: nextValue };
      nextForm.loanDetailsJson = buildLoanDetailsJsonFromSelection(
        documentationSelection,
        nextForm.productType,
        propertyDetails,
        assetSecuredDetails,
        assetUnsecuredDetails
      );
      return nextForm;
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
    setApiError("");
  };



  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!loanProduct?.loanProductId) {
      setApiError("Loan product id is missing.");
      toast.error("Loan product id is missing.");
      return;
    }

    const validationErrors = validateLoanProductForm(form);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please correct validation errors.");
      return;
    }

    setSubmitting(true);
    setApiError("");

    const payload = buildLoanProductPayload(form, loanProduct.loanProductId);

    try {
      await api.put("/api/Lenders/Updateloan-products", payload);
      toast.success("Loan product updated successfully.");
      navigate("/CRMLoanProducts");
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to update loan product.";
      setApiError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-4"><Spinner color="primary" /></div>;

  return (
    <div className="px-4 py-4" style={{ minHeight: "300px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
      <Form onSubmit={handleSubmit} style={{ boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", backgroundColor: "white", borderRadius: "8px", padding: "20px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="f-w-900 m-0 text-primary">Edit Loan Product</h3>
        </div>

        {!!apiError && <div className="alert alert-danger py-2">{apiError}</div>}

        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Lender *</label>
            <select className={`form-control ${errors.lenderId ? "is-invalid" : ""}`} value={form.lenderId} onChange={(e) => handleChange("lenderId", e.target.value)}>
              <option value="">Select Lender</option>
              {lenders.map((lender) => <option key={lender.lenderId} value={lender.lenderId}>{lender.lenderName}</option>)}
            </select>
            {errors.lenderId && <div className="invalid-feedback">{errors.lenderId}</div>}
          </div>

          <div className="col-md-3">
            <label className="form-label">Product Name *</label>
            <input className={`form-control ${errors.productName ? "is-invalid" : ""}`} type="text" maxLength={100} value={form.productName} onChange={(e) => handleChange("productName", e.target.value)} />
            {errors.productName && <div className="invalid-feedback">{errors.productName}</div>}
          </div>

          <div className="col-md-3">
            <label className="form-label">Product Type *</label>
            <select className={`form-control ${errors.productType ? "is-invalid" : ""}`} value={form.productType} onChange={(e) => handleChange("productType", e.target.value)}>
              <option value="">Select Product Type</option>
              {productTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {errors.productType && <div className="invalid-feedback">{errors.productType}</div>}
          </div>

          {form.productType === "Property" && <PropertyProductTypeFields details={propertyDetails} onChange={setPropertyDetails} />}
          {form.productType === "Asset - Secured" && <AssetSecuredProductTypeFields details={assetSecuredDetails} onChange={setAssetSecuredDetails} />}
          {form.productType === "Asset - Unsecured" && <AssetUnsecuredProductTypeFields details={assetUnsecuredDetails} onChange={setAssetUnsecuredDetails} />}




        </div>

        <div className="d-flex gap-2 mt-4">
          <Button type="button" color="secondary" onClick={() => navigate("/CRMLoanProducts")}>Back to Loan Products</Button>
          <Button type="submit" color="primary" disabled={submitting}>{submitting ? "Updating..." : "Update Loan Product"}</Button>
        </div>
      </Form>
    </div>
  );
};

export default EditCRMLoanProduct;







